import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://emailportalapi.twilightfinland.eu/api';

// Async Thunks
export const fetchChatRooms = createAsyncThunk(
    'chat/fetchChatRooms',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            const config = {
                headers: { Authorization: `Bearer ${auth.token}` },
            };
            const response = await axios.get(`${API_URL}/chat/rooms`, config);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch chat rooms');
        }
    }
);

export const fetchMessages = createAsyncThunk(
    'chat/fetchMessages',
    async ({ roomId, page = 1 }, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            const config = {
                headers: { Authorization: `Bearer ${auth.token}` },
            };
            const response = await axios.get(`${API_URL}/chat/messages/${roomId}?page=${page}`, config);
            return { roomId, data: response.data }; // Return roomId to map messages
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch messages');
        }
    }
);

export const createChatRoom = createAsyncThunk(
    'chat/createChatRoom',
    async (participantId, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            const config = {
                headers: { Authorization: `Bearer ${auth.token}` },
            };
            const response = await axios.post(`${API_URL}/chat/room`, { participantId }, config);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create chat room');
        }
    }
);

export const markMessagesRead = createAsyncThunk(
    'chat/markMessagesRead',
    async (roomId, { getState, rejectWithValue }) => {
        try {
            const { auth } = getState();
            const config = {
                headers: { Authorization: `Bearer ${auth.token}` },
            };
            await axios.put(`${API_URL}/chat/read/${roomId}`, {}, config);
            return roomId;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    rooms: [],
    activeRoom: null,
    activeRoomMessages: [],
    activeRoomPagination: { page: 1, totalPages: 1, total: 0 },
    isLoading: false,
    error: null,
    isChatOpen: false, // UI state
};

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        toggleChat: (state) => {
            state.isChatOpen = !state.isChatOpen;
        },
        setActiveRoom: (state, action) => {
            state.activeRoom = action.payload;
            state.activeRoomMessages = []; // Clear on switch
            // Recalculate unread in rooms list? Handled by component dispatching markRead
        },
        addMessage: (state, action) => {
            const message = action.payload;
            // If message belongs to active room
            if (state.activeRoom && state.activeRoom.id === message.chatRoomId) {
                state.activeRoomMessages.push(message);
            }

            // Update last message in rooms list
            const roomIndex = state.rooms.findIndex(r => r.id === message.chatRoomId);
            if (roomIndex !== -1) {
                const room = state.rooms[roomIndex];
                room.lastMessage = message.message;
                room.lastMessageAt = new Date().toISOString(); // Approximate
                if (state.activeRoom?.id !== message.chatRoomId) {
                    room.unreadCount = (room.unreadCount || 0) + 1;
                }

                // Move to top
                state.rooms.splice(roomIndex, 1);
                state.rooms.unshift(room);
            }
        },
        updateReadStatus: (state, action) => {
            const roomId = action.payload;
            // Update unread count in rooms list
            const room = state.rooms.find(r => r.id === roomId);
            if (room) {
                room.unreadCount = 0;
            }

            // If this implies that *my* messages were read by the other person (which is what double tick means),
            // then we need to update `isRead` to true for all my messages in this room.
            // However, the socket event name `messages_read` usually implies "messages in this room have been read".
            // So we update the local message state to show double ticks.
            if (state.activeRoom && state.activeRoom.id === roomId) {
                state.activeRoomMessages = state.activeRoomMessages.map(msg => ({
                    ...msg,
                    isRead: true
                }));
            }
        },
        clearChatState: (state) => {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Rooms
            .addCase(fetchChatRooms.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchChatRooms.fulfilled, (state, action) => {
                state.isLoading = false;
                state.rooms = action.payload;
            })
            .addCase(fetchChatRooms.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Fetch Messages
            .addCase(fetchMessages.fulfilled, (state, action) => {
                const { data, roomId } = action.payload;
                // If loading same room, append/replace
                if (state.activeRoom && state.activeRoom.id == roomId) {
                    // If page 1, replace. If > 1, prepend (not implemented for simplicity, defaulting to replace for now)
                    state.activeRoomMessages = data.messages;
                    state.activeRoomPagination = {
                        page: data.page,
                        totalPages: data.totalPages,
                        total: data.total
                    };
                }
            })

            // Create Room
            .addCase(createChatRoom.fulfilled, (state, action) => {
                state.activeRoom = action.payload;
                // Check if exists
                if (!state.rooms.find(r => r.id === action.payload.id)) {
                    state.rooms.unshift(action.payload);
                }
                state.isChatOpen = true;
            })

            // Mark Read
            .addCase(markMessagesRead.fulfilled, (state, action) => {
                const roomId = action.payload;
                const room = state.rooms.find(r => r.id === roomId);
                if (room) room.unreadCount = 0;
            });
    },
});

export const { toggleChat, setActiveRoom, addMessage, updateReadStatus, clearChatState } = chatSlice.actions;

export default chatSlice.reducer;
